import React from 'react';
import { Store } from '@profitfy/pakkun-icons';
import { useParams } from 'react-router-dom';

import { EHeadingSize, EHeadingWeight } from '@domain/enums/components/EHeading';
import { EInfoProductProvider } from '@domain/enums/common/integrations/EInfoProductProvider';
import { EAlertVariant } from '@domain/enums/components/EAlert';
import { ISidemodalProps } from '@domain/interfaces/onboarding/integrations/ISidemodal';
import { IParams } from '@domain/interfaces/IParams';

import { useMediaQueryHeight } from '@helpers/hooks/useMediaQuery';
import { useInfoProduct } from '@helpers/hooks/pages/onboarding/useInfoProduct';
import { useConfig } from '@helpers/hooks/useConfig';

import { INFO_PRODUCT_PROVIDERS } from '@constants/common/integrations';

import SuggestionModal from './SuggestionModal';

import Save from './Save';

import * as S from './styles';

const SideModal: React.FC<ISidemodalProps> = ({ isOpen, toggle }) => {
  const {
    selectedPlatform: userSelectedPlatform,
    storeIntegrations,
    handleSelectedPlatform,
  } = useInfoProduct();
  const { user, analytics } = useConfig();
  const { storeAliasId } = useParams<IParams>();

  const mobileHeight = useMediaQueryHeight('768px');

  const [selectedPlatform, setSelectedPlatform] = React.useState<EInfoProductProvider | undefined>(
    undefined,
  );
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = React.useState<boolean>(false);

  const handleSuggestionModalOpen = React.useCallback(
    () => setIsSuggestionModalOpen(!isSuggestionModalOpen),
    [isSuggestionModalOpen],
  );

  const openSuggestionModal = React.useCallback(() => {
    handleSuggestionModalOpen();
  }, [handleSuggestionModalOpen]);

  const onChange = React.useCallback(event => {
    setSelectedPlatform(event.target.value);
  }, []);

  const onSave = React.useCallback(() => {
    const foundProvider = INFO_PRODUCT_PROVIDERS.find(
      integration => integration.identifier === selectedPlatform,
    );

    handleSelectedPlatform(selectedPlatform);

    analytics?.track(
      'Onboarding Selected E-commerce',
      {
        ecommerce_provider: foundProvider?.name,
        email: user?.email,
      },
      { context: { groupId: storeAliasId } },
    );
    analytics?.trackHubSpot(storeAliasId, { ecom_platform: foundProvider?.name });

    toggle();
  }, [selectedPlatform, handleSelectedPlatform, toggle, analytics, user, storeAliasId]);

  const hasProviderIntegration = storeIntegrations?.store_front.find(
    provider => provider.is_active,
  );

  return (
    <>
      <S.Sidemodal isOpen={isOpen} toggle={toggle} blockOverflowYScroll={mobileHeight}>
        <S.Header>
          <Store size={24} />
          <S.Title fontWeight={EHeadingWeight.REGULAR} type={EHeadingSize.H4}>
            Selecionar plataformas de infoproduto
          </S.Title>
        </S.Header>

        <S.Body>
          <S.Alert variant={EAlertVariant.WARNING} flexStart>
            Atualmente atendemos apenas transações <strong>realizadas em Real (BRL).</strong>
            <br />
            Estamos estudando a possibilidade de expandir o serviço para as demais moedas.
          </S.Alert>

          <S.NoIntegrationText>
            Não encontrou a plataforma que você usa?{' '}
            <S.SuggestionButton onClick={openSuggestionModal}>
              Nos informe clicando aqui.
            </S.SuggestionButton>
          </S.NoIntegrationText>

          <S.IntegrationsWrapper>
            {INFO_PRODUCT_PROVIDERS.map(integration => {
              const Icon = integration.icon;

              return (
                <S.InputGroup key={integration.identifier}>
                  <S.RadioLabel>
                    <S.RadioTextWrapper>
                      <Icon size={24} />
                      <S.RadioText>{integration.name}</S.RadioText>

                      {(integration.identifier === EInfoProductProvider.EDUZZ ||
                        integration.identifier === EInfoProductProvider.MONETIZZE ||
                        integration.identifier === EInfoProductProvider.PAYT) && (
                        <S.BetaBadge>Beta</S.BetaBadge>
                      )}
                    </S.RadioTextWrapper>

                    <S.Radio
                      value={integration.identifier}
                      name="platform"
                      defaultChecked={
                        selectedPlatform
                          ? selectedPlatform === integration.identifier
                          : userSelectedPlatform === integration.identifier
                      }
                      onChange={onChange}
                      id={`${integration.identifier}-option`}
                      disabled={Boolean(hasProviderIntegration)}
                    />
                  </S.RadioLabel>
                </S.InputGroup>
              );
            })}
          </S.IntegrationsWrapper>
        </S.Body>

        <Save onCancel={toggle} onSave={onSave} />
      </S.Sidemodal>

      <SuggestionModal isOpen={isSuggestionModalOpen} toggle={handleSuggestionModalOpen} />
    </>
  );
};

export default SideModal;
